import { API_ORIGIN, apiUrl } from "./apiOrigin";
import InspectorSOPStaffGrid from './InspectorSOPStaffGrid';
import React, { useEffect, useState } from 'react';
import ComplianceNextDue from './ComplianceNextDue';
import { TrainingSettings, TrainingResults } from './ComplianceTraining';
const API = apiUrl("/api/headoffice/compliance-grid/sop-management");
export const isSopManagement = row => ['sop-held', 'sop-scope', 'sop-staff-read', 'sop-version-control', 'sop-access'].includes(String(row?.elementId || row?.rowId || row || '').replace(/^el:/, ''));
async function api(token, path, body) {
  const r = await fetch(API + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token') || ''}`
    },
    body: JSON.stringify(body)
  });
  const d = await r.json();
  if (!r.ok) throw Error(d.error || 'Unable to process SOP management');
  return d;
}
const button = {
  padding: '7px 12px',
  border: '1px solid #94a3b8',
  borderRadius: 5,
  background: '#fff',
  color: '#172554',
  cursor: 'pointer'
};
const modal = {
  position: 'fixed',
  inset: 0,
  zIndex: 10100,
  background: 'rgba(15,23,42,.45)',
  padding: '4vh 16px',
  overflow: 'auto'
};
const date = v => v ? new Date(v).toLocaleDateString('en-GB') : 'Not recorded';
export function SopManagementConfig({
  row,
  token,
  onClose,
  onSaved
}) {
  const rowId = row.rowId,
    scope = rowId === 'el:sop-scope',
    access = rowId === 'el:sop-access';
  const [data, setData] = useState(null),
    [mode, setMode] = useState(scope ? 'task' : 'sop'),
    [chosen, setChosen] = useState([]),
    [recipients, setRecipients] = useState({}),
    [descriptions, setDescriptions] = useState({}),
    [reviewer, setReviewer] = useState(''),
    [statement, setStatement] = useState(''),
    [link, setLink] = useState(''),
    [days, setDays] = useState(365),
    [training, setTraining] = useState({}),
    [attachment, setAttachment] = useState(null),
    [replaceOpen, setReplaceOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    let off = false;
    api(token, '/load', {
      rowId
    }).then(d => {
      if (off) return;
      setData(d);
      setMode(d.row.defaultSource === 'link' ? access ? 'description' : 'link' : scope ? 'task' : d.row.defaultSource || 'sop');
      setChosen(d.scope?.dbNames || d.branches.filter(b => b.jurisdiction?.nation).map(b => b.dbName));
      setRecipients(Object.fromEntries(d.cells.map(c => [c.dbName, c.recipientStaffId || ''])));
      setDescriptions(Object.fromEntries(d.cells.map(c => [c.dbName, c.inspectorNote || ''])));
      setReviewer(d.scope ? `${d.scope.reviewerDb}|${d.scope.reviewerId}` : '');
      setStatement(d.scope?.notes || '');
      setLink(d.cells.find(c => c.linkUrl)?.linkUrl || '');
      setTraining(d.row.training || {});
      setDays(d.row.validity?.days || 365);
    }).catch(e => setError(e.message));
    return () => {
      off = true;
    };
  }, [rowId, token, access, scope]);
  const save = async send => {
    setBusy(true);
    setError('');
    try {
      const [reviewerDb, reviewerId] = reviewer.split('|');
      const r = await api(token, '/save', {
        rowId,
        mode,
        dbNames: chosen,
        recipients,
        descriptions,
        reviewerDb,
        reviewerId,
        statement,
        link,
        days: Number(days),
        training,
        trainingAttachment: attachment,
        replaceOpen,
        send
      });
      await onSaved?.(`${row.label}: saved; ${(r.results || []).filter(x => x.sent).length} task(s) sent. ${(r.results || []).map(x => x.skipped).filter(Boolean).join('; ')}`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return <div role="dialog" aria-modal="true" aria-label={`Settings — ${row.label}`} style={modal}><div style={{
      background: '#fff',
      maxWidth: 1120,
      margin: 'auto',
      padding: 20,
      borderRadius: 10
    }}>
  <h3>{row.label}</h3>{error && <p role="alert" style={{
        color: '#b91c1c'
      }}>{error}</p>}
  {!data ? <p>Loading…</p> : <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
        gap: 20
      }}>
   <section><h4>1 · About this item</h4><p>{row.help || row.explanation}</p><p>Evidence is shared by the GPhC and PSNI views. Branch-specific requirements and exceptions still need review.</p><h4>Branches covered</h4>{data.branches.map(b => <label key={b.dbName} style={{
            display: 'block',
            marginBottom: 6
          }}><input type="checkbox" disabled={!b.jurisdiction?.nation} checked={chosen.includes(b.dbName)} onChange={e => setChosen(e.target.checked ? [...chosen, b.dbName] : chosen.filter(n => n !== b.dbName))} />{b.pharmacyName}{!b.jurisdiction?.nation ? ' — resolve postcode first' : ''}</label>)}</section>
   <section><h4>2 · Where the evidence comes from</h4>
    {scope ? <p>One selected reviewer acknowledges scope completeness for all ticked branches. One task is issued, not one per branch.</p> : <>
     <label style={{
              display: 'block',
              margin: 8
            }}><input type="radio" checked={mode === 'sop'} onChange={() => setMode('sop')} /> Pharmace SOP system</label>
     {access ? <label style={{
              display: 'block',
              margin: 8
            }}><input type="radio" checked={mode === 'description'} onChange={() => setMode('description')} /> Held elsewhere — branch access description</label> : <>
      <label style={{
                display: 'block',
                margin: 8
              }}><input type="radio" checked={mode === 'link'} onChange={() => setMode('link')} /> Held elsewhere — supporting document/process link</label>
      <label style={{
                display: 'block',
                margin: 8
              }}><input type="radio" checked={mode === 'task'} onChange={() => setMode('task')} /> Held elsewhere — task + email to upload document(s)</label>
     </>}
    </>}
    {mode === 'link' && <label>External evidence link<input aria-label="External SOP evidence link" value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" style={{
              width: '100%'
            }} /></label>}
    {mode === 'sop' && <p>The branch’s live SOP list is used automatically. Contents, versions, review dates and assignments are available in cell and inspector details.</p>}
    {mode === 'task' && <TrainingSettings value={training} attachment={attachment} onChange={setTraining} onAttachment={setAttachment} />}
   </section>
   <section><h4>3 · Settings</h4>
    {scope ? <><label>Estate scope reviewer<select aria-label="Estate scope reviewer" value={reviewer} onChange={e => setReviewer(e.target.value)} style={{
                width: '100%'
              }}><option value="">Choose reviewer…</option>{data.reviewers.map(p => <option key={`${p.dbName}|${p.staffId}`} value={`${p.dbName}|${p.staffId}`}>{p.name} · {p.role} · {p.branch}</option>)}</select></label><label style={{
              display: 'block',
              marginTop: 10
            }}>Activities, branch-specific scope and exceptions to resolve<textarea aria-label="SOP scope notes" value={statement} onChange={e => setStatement(e.target.value)} style={{
                width: '100%',
                minHeight: 100
              }} /></label><p>Acknowledgement confirms that the listed branches’ scope has been reviewed and any exceptions resolved.</p><label><input type="checkbox" checked={replaceOpen} onChange={e => setReplaceOpen(e.target.checked)} /> Replace an existing pending scope task if reviewer or coverage changes</label></> : chosen.map(n => <div key={n} style={{
            marginBottom: 12
          }}><b>{data.branches.find(b => b.dbName === n)?.pharmacyName}</b>{mode === 'task' && <select aria-label={`Recipient ${n}`} value={recipients[n] || ''} onChange={e => setRecipients({
              ...recipients,
              [n]: e.target.value
            })} style={{
              width: '100%'
            }}><option value="">Choose recipient…</option>{data.staff.filter(p => p.dbName === n).map(p => <option key={p.staffId} value={p.staffId}>{p.name} · {p.role}</option>)}</select>}{mode === 'description' && <textarea aria-label={`SOP access ${n}`} value={descriptions[n] || ''} onChange={e => setDescriptions({
              ...descriptions,
              [n]: e.target.value
            })} placeholder="Where held; how staff, locums and temporary staff obtain access" style={{
              width: '100%',
              minHeight: 100
            }} />}</div>)}
    {mode === 'task' && <label>Review/renewal interval (days) <input aria-label="SOP review interval" type="number" min="1" max="3650" value={days} onChange={e => setDays(e.target.value)} /></label>}
   </section>
  </div>}
  <div style={{
        display: 'flex',
        gap: 8,
        marginTop: 16
      }}><button style={button} disabled={busy || !data} onClick={() => save(true)}>Save & apply to selected branches</button><button style={button} disabled={busy} onClick={onClose}>Cancel</button></div>
  <p style={{
        fontSize: 12
      }}>Applying sends the configured task and email for external evidence, or the single estate scope task. An existing open task is not duplicated.</p>
 </div></div>;
}
export function SopManagementDetails({
  row,
  dbName,
  token,
  readOnly = false,
  onChanged
}) {
  const [cell, setCell] = useState(null),
    [error, setError] = useState(''),
    [loaded, setLoaded] = useState(false),
    [configure, setConfigure] = useState(false);
  const load = async () => {
    try {
      const d = await api(token, '/details', {
        rowId: row.rowId,
        dbName
      });
      setCell(d.cell);
      setLoaded(true);
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => {
    load();
  }, [row.rowId, dbName, token]);
  const c = cell || {};
  const files = c.evidence?.files?.length ? c.evidence.files : c.evidence?.fileUrl ? [{
    fileUrl: c.evidence.fileUrl,
    filename: c.evidence.filename || 'Supporting evidence'
  }] : [];
  return <section style={{
    padding: 12,
    fontSize: 13
  }}><h3>{row.label}</h3>{error && <p role="alert">{error}</p>}{!loaded && !error && <p>Loading…</p>}{loaded && !cell && <p>This branch’s SOP check has not been configured yet.</p>}
  {c.sopStatement && <p>{c.sopStatement}</p>}{c.inspectorNote && <p><b>Branch arrangements:</b> {c.inspectorNote}</p>}
  {!!c.scopeBranches?.length && <><p><b>Reviewer:</b> {c.recipientName}</p><p><b>Branches covered:</b> {c.scopeBranches.map(b => b.name).join(', ')}</p><p>{c.scopeStatement}</p></>}
  {row.rowId === 'el:sop-staff-read' && c.source === 'sop' && <InspectorSOPStaffGrid pharmacyId={dbName} asOfDate={new Date().toISOString().slice(0, 10)} token={token} />}
  {row.rowId !== 'el:sop-staff-read' && !!c.sopDocuments?.length && <table style={{
      width: '100%',
      borderCollapse: 'collapse'
    }}><thead><tr>{['SOP / contents', 'Version', 'Next review'].map(x => <th key={x} style={{
            textAlign: 'left',
            padding: 6
          }}>{x}</th>)}</tr></thead><tbody>{c.sopDocuments.map((s, i) => <React.Fragment key={s.title + i}><tr><td style={{
              padding: 6
            }}>{s.fileUrl ? <a href={s.fileUrl} target="_blank" rel="noreferrer">{s.title}</a> : <>{s.title} — document file missing</>}</td><td>{s.version || 'Not recorded'}</td><td>{date(s.expiryDate)}</td></tr></React.Fragment>)}</tbody></table>}
  {c.source === 'sop' && !c.sopDocuments?.length && <p>No live SOPs found at this branch.</p>}
  {c.linkUrl && <p><a href={c.linkUrl} target="_blank" rel="noreferrer">Open external evidence/process</a></p>}{files.map((f, i) => <p key={i}><a href={f.fileUrl} target="_blank" rel="noreferrer">{f.filename || 'View supporting document'}</a></p>)}
  {c.task && <p>Task: {c.task.status}{c.task.recipientName ? ` — ${c.task.recipientName}` : ''}</p>}
  <ComplianceNextDue cell={c} rowId={row.rowId} dbName={c.scopeMasterDbName || dbName} token={token} readOnly={readOnly} onChanged={async () => {
      await load();
      await onChanged?.();
    }} /><TrainingResults cell={c} />
  {!readOnly && <button style={button} onClick={() => setConfigure(true)}>Configure SOP check</button>}{configure && <SopManagementConfig row={row} token={token} onClose={() => setConfigure(false)} onSaved={async () => {
      await load();
      await onChanged?.();
    }} />}
 </section>;
}
